/**
 * Getting to the site (F4).
 *
 * Pure, so it is testable without a phone: take the site row the device holds
 * and say where Google Maps and Waze should be pointed. Coordinates win when
 * the site has them, because a street address is a guess at a gate and a
 * latitude/longitude is not; with neither a usable address nor coordinates
 * there is nothing honest to navigate to and the caller shows no button.
 */

export type SiteLocation = {
  address_line?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
};

export type NavigationLinks = {
  /** What is being navigated to, for display. */
  label: string;
  google: string;
  waze: string;
};

function clean(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function coordinate(value: unknown, limit: number): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || Math.abs(n) > limit) return null;
  return n;
}

/** "12 Main Rd, Seacliff Park SA 5049" — whatever of it is filled in. */
export function siteAddress(site: SiteLocation | null | undefined): string {
  if (!site) return "";
  const street = clean(site.address_line);
  const locality = [clean(site.suburb), clean(site.state), clean(site.postcode)].filter(Boolean).join(" ");
  return [street, locality].filter(Boolean).join(", ");
}

/**
 * Map links for a site, or null when there is nothing to navigate to. An
 * address needs a street or a suburb: a bare state and postcode would send a
 * tech to the middle of a postcode.
 */
export function navigationLinks(site: SiteLocation | null | undefined): NavigationLinks | null {
  if (!site) return null;
  const address = siteAddress(site);
  const lat = coordinate(site.latitude, 90);
  const lng = coordinate(site.longitude, 180);
  // 0,0 is what an ungeocoded row looks like, not a place a van can go.
  if (lat !== null && lng !== null && !(lat === 0 && lng === 0)) {
    const pair = `${lat},${lng}`;
    return {
      label: address || pair,
      google: `https://www.google.com/maps/dir/?api=1&destination=${pair}`,
      waze: `https://waze.com/ul?ll=${pair}&navigate=yes`,
    };
  }
  if (!clean(site.address_line) && !clean(site.suburb)) return null;
  const q = encodeURIComponent(address);
  return {
    label: address,
    google: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    waze: `https://waze.com/ul?q=${q}&navigate=yes`,
  };
}
